import { Controller } from "@hotwired/stimulus"
import { get, post } from "@rails/request.js"
import { syncGateReason } from "../lib/gate_reason"
import { setButtonLoading } from "../lib/button_loading"
import { statusBadgeClass } from "../lib/status_badge"

// Trust Center — Review & Submit (#18737, Phase 3a of #18673)
//
// When the step opens, runs the two checks BEFORE anything goes to Twilio:
//   * the pre-check (GET pre_check → TrustCenter::PreCheck): every failed row
//     is a PROBLEM — the live opt-in page among them — and keeps Submit to
//     Carriers disabled;
//   * the AI review (POST ai_review → TrustCenterReviewService): its findings
//     are SUGGESTIONS and never block.
// Submit to Carriers is a Ready Gate (PATTERN-3): disabled until the pre-check
// has passed and "I agree" is ticked, with a line naming what's left. It POSTs
// submit_to_carriers (TrustCenter::CarrierSubmission, which re-checks both on
// the server) and, once sent, dispatches `submitted` for the wizard controller
// (#carriersSubmitted) to move to Carrier Approval.
//
// Like the AI review it replaced, nothing runs on connect(): every wizard panel
// connects on page load, and the pre-check loads the agent's live opt-in page.
// The checks run the first time this panel is shown (.active), and again each
// time the agent comes back to it after editing another step.
//
// Rows are cloned from the server-rendered template and filled with textContent
// (never innerHTML): messages and suggestions are server- and model-derived.
const PENDING = "Running the pre-check…"
const AGREE = "Tick “I Agree” to submit"

export default class extends Controller {
  static targets = ["checks", "suggestions", "verdict", "optInPill", "agree", "submitButton", "hint", "message",
    "rowTemplate"]

  static values = {
    preCheckUrl: String,
    reviewUrl: String,
    submitUrl: String,
    sent: Boolean,
  }

  connect() {
    this.preCheck = null
    this.running = false
    this.busy = false
    this.panel = this.element.closest(".trust-center-panel")
    if (this.sentValue) return

    if (this.isPanelActive()) {
      this.runChecks()
    } else if (this.panel) {
      this.wasActive = false
      this.observer = new MutationObserver(() => {
        const active = this.isPanelActive()
        if (active && !this.wasActive) this.runChecks()
        this.wasActive = active
      })
      this.observer.observe(this.panel, { attributes: true, attributeFilter: ["class"] })
    }
  }

  disconnect() {
    this.observer?.disconnect()
  }

  isPanelActive() {
    return this.panel ? this.panel.classList.contains("active") : true
  }

  async runChecks() {
    if (this.running) return
    this.running = true
    this.preCheck = null
    this.renderPending()
    this.refreshGate()
    try {
      await Promise.all([this.loadPreCheck(), this.loadSuggestions()])
    } finally {
      this.running = false
      this.refreshGate()
    }
  }

  async loadPreCheck() {
    try {
      const response = await get(this.preCheckUrlValue, { responseKind: "json" })
      const data = (await response.json) || {}
      this.applyPreCheck(response.ok && Array.isArray(data.checks) ? data : null)
    } catch {
      this.applyPreCheck(null)
    }
  }

  async loadSuggestions() {
    try {
      const response = await post(this.reviewUrlValue, { contentType: "application/json", responseKind: "json" })
      const data = (await response.json) || {}
      this.renderSuggestions(data)
    } catch {
      this.renderSuggestions({ available: false })
    }
  }

  // null = the pre-check itself could not run: treated as a problem (Submit
  // stays closed) — the server would re-run it on submit anyway.
  applyPreCheck(data) {
    this.preCheck = data || {
      ready: false,
      checks: [{ key: "pre_check", label: "Pre-check", ok: false, message: "We couldn't run the pre-check. Reload the page to try again.", fix_step: null }],
    }
    this.renderChecks(this.preCheck.checks)
  }

  // ----- Ready Gate (PATTERN-3) -------------------------------------------

  readinessItems() {
    if (this.running || !this.preCheck) return [PENDING]
    const problems = this.preCheck.checks.filter((check) => !check.ok)
    if (problems.length) return problems.map((check) => check.label)
    if (!this.hasAgreeTarget || !this.agreeTarget.checked) return [AGREE]
    return []
  }

  refreshGate() {
    if (!this.hasSubmitButtonTarget) return
    const items = this.readinessItems()
    let reason = ""
    if (items[0] === PENDING || items[0] === AGREE) reason = items[0]
    else if (items.length) reason = `Fix ${items.length} ${items.length === 1 ? "problem" : "problems"}: ${items.join(" · ")}`
    if (this.hasHintTarget) this.hintTarget.textContent = reason
    syncGateReason(this.submitButtonTarget, { ready: items.length === 0 && !this.busy, reason: reason || "Sending…" })
  }

  // ----- Submit to Carriers ----------------------------------------------

  async submit(event) {
    event.preventDefault()
    if (this.busy || this.readinessItems().length) return
    this.busy = true
    setButtonLoading(this.submitButtonTarget, true)
    this.showMessage("", "info")
    let data = {}
    try {
      const response = await post(this.submitUrlValue, {
        body: JSON.stringify({ agreed: this.agreeTarget.checked }),
        contentType: "application/json",
        responseKind: "json",
      })
      data = (await response.json) || {}
    } catch {
      data = { success: false, message: "Something went wrong. Please try again." }
    } finally {
      this.busy = false
      setButtonLoading(this.submitButtonTarget, false)
    }
    this.applySubmitResult(data)
  }

  applySubmitResult(data) {
    if (data.success) {
      // Sent: the button never re-opens (the wizard reloads onto Carrier Approval).
      this.submitButtonTarget.disabled = true
      this.showMessage(data.message || "Sent to the carriers.", "success")
      this.dispatch("submitted", { detail: data })
      return
    }
    if (data.pre_check) this.applyPreCheck(data.pre_check)
    this.showMessage(data.message || "Something went wrong. Please try again.", data.guarded ? "info" : "error")
    this.refreshGate()
  }

  // ----- Rendering --------------------------------------------------------

  renderPending() {
    if (this.hasChecksTarget) {
      this.checksTarget.replaceChildren(this.row({ icon: "ph-spinner", title: PENDING }))
    }
    if (this.hasSuggestionsTarget) this.suggestionsTarget.replaceChildren()
    this.setVerdict("Checking", "secondary")
    this.setOptInPill("Checking…", "secondary")
  }

  renderChecks(checks) {
    if (!this.hasChecksTarget) return
    this.checksTarget.replaceChildren(
      ...checks.map((check) =>
        this.row({
          // A passing row with a note was not checked (the opt-in page outside production).
          icon: check.ok ? (check.note ? "ph-info" : "ph-check-circle") : "ph-x-circle",
          tone: check.ok ? (check.note ? null : "done") : "danger",
          title: check.label,
          line: check.ok ? check.note : check.message,
          fixStep: check.ok ? null : check.fix_step,
          fixUrl: check.ok ? null : check.fix_url,
          key: check.key,
        })
      )
    )
    const problems = checks.filter((check) => !check.ok).length
    if (problems) this.setVerdict(`${problems} ${problems === 1 ? "Problem" : "Problems"}`, "danger")
    else this.setVerdict("Ready", "success")

    const optIn = checks.find((check) => check.key === "opt_in_page")
    if (optIn?.ok && optIn.note) this.setOptInPill("Not Checked", "secondary")
    else if (optIn) this.setOptInPill(optIn.ok ? "Live · Checked" : "Problem", optIn.ok ? "success" : "danger")
  }

  // AI findings that need attention, as suggestions; nothing when all clear or unavailable.
  renderSuggestions(data) {
    if (!this.hasSuggestionsTarget) return
    if (!data.available) {
      this.suggestionsTarget.replaceChildren()
      return
    }
    const fields = (data.fields || []).filter((field) => !field.ok)
    this.suggestionsTarget.replaceChildren(
      ...fields.map((field) =>
        this.row({
          icon: "ph-lightbulb",
          tone: "time",
          title: `Suggestion: ${field.label}`,
          line: [field.warning, field.suggested_fix && `Try: ${field.suggested_fix}`].filter(Boolean).join(" "),
          fixStep: field.editable && typeof field.step === "number" ? field.step : null,
          key: `suggestion-${field.field}`,
        })
      )
    )
  }

  // One row of shared/_need_to_know (PATTERN #8), cloned from the server-rendered
  // <template> so the markup has one source (#19809). tone: "done" | "time" |
  // "danger" | null (neutral).
  row({ icon, tone, title, line, fixStep, fixUrl, key }) {
    const li = this.rowTemplateTarget.content.querySelector(".gl-need-to-know__item").cloneNode(true)
    if (key) li.dataset.check = key

    li.querySelector(".gl-need-to-know__icon").className =
      `gl-need-to-know__icon gl-need-to-know__icon--${tone || "neutral"} ph ${icon}`
    const body = li.querySelector(".gl-need-to-know__body")
    body.querySelector(".gl-need-to-know__title").textContent = title
    const text = body.querySelector(".gl-need-to-know__text")
    if (line) text.textContent = line
    else text.remove()
    // "Fix It" jumps to a step the agent can still edit (wizard#goToStep).
    if (typeof fixStep === "number") {
      const fix = document.createElement("button")
      fix.type = "button"
      fix.className = "gl-link gl-review-list__edit"
      fix.textContent = "Fix It"
      fix.dataset.action = "trust-center-wizard#goToStep"
      fix.dataset.step = String(fixStep)
      body.appendChild(fix)
    } else if (fixUrl) {
      // A fix outside the wizard (the website's Agency Name, #20123) opens its own page.
      const fix = document.createElement("a")
      fix.className = "gl-link gl-review-list__edit"
      fix.textContent = "Fix It"
      fix.href = fixUrl
      body.appendChild(fix)
    }
    return li
  }

  setVerdict(label, tone) {
    if (!this.hasVerdictTarget) return
    this.verdictTarget.textContent = label
    // The header's aside slot places it (gl_section_header aside:); --on-dark keeps it legible there.
    this.verdictTarget.className = `${statusBadgeClass(tone)} gl-status-badge--on-dark`
  }

  setOptInPill(label, tone) {
    if (!this.hasOptInPillTarget) return
    this.optInPillTarget.textContent = label
    this.optInPillTarget.className = statusBadgeClass(tone)
  }

  showMessage(text, tone) {
    if (!this.hasMessageTarget) return
    const toneClass = { success: "gl-text-success", info: "text-muted", error: "gl-text-danger" }[tone] || "text-muted"
    this.messageTarget.textContent = text
    this.messageTarget.className = `gl-form-hint mt-2 mb-0 ${toneClass}`
  }
}
